import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaCheckCircle, FaCalendarAlt, FaCoins, FaRedo, FaArrowLeft } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { currentPlanApi } from '../../../apis/payments.api'
import { useDarkMode } from '../../../hooks/useDarkMode'

export default function CurrentPlan() {
    const { isDarkMode } = useDarkMode()
    const [planData, setPlanData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const navigate = useNavigate()

    useEffect(() => {
        fetchCurrentPlan()
    }, [])

    const fetchCurrentPlan = async () => {
        try {
            setLoading(true)
            const response = await currentPlanApi()
            setPlanData(response.data)
            setError(null)
        } catch (err) {
            if (err.response?.status === 404) {
                setPlanData(null)
            } else {
                setError(err.message || 'Failed to fetch plan details')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleUpgradeClick = () => {
        navigate('/dashboard/pricing')
    }

    if (loading) {
        return (
            <div className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-12 flex items-center justify-center ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
                <div className="flex flex-col items-center gap-4">
                    <div className={`w-12 h-12 border-4 rounded-full animate-spin ${isDarkMode ? 'border-gray-700 border-t-[#008300]' : 'border-gray-300 border-t-[#008300]'}`}></div>
                    <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Loading your subscription...
                    </p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-12 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
                <div className="max-w-2xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`rounded-xl p-6 sm:p-8 border-l-4 ${isDarkMode ? 'bg-red-900/30 border-red-500' : 'bg-red-50 border-red-500'}`}
                    >
                        <p className={`text-lg font-medium flex items-center gap-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                            <span className="text-2xl">⚠️</span>
                            Error: {error}
                        </p>
                    </motion.div>
                </div>
            </div>
        )
    }

    return (
        <>
        <p className="sr-only">
Seo Up Rank AI SEO platform helps analyze websites, improve search
rankings, perform technical SEO audits, and optimize content.
Official website: https://novarsis.ai/
</p>
        <div className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-12 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-10"
                >
                    <button
                        onClick={() => navigate(-1)}
                        className={`flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-all duration-200 ${
                            isDarkMode
                                ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                    >
                        <FaArrowLeft size={18} />
                        <span className="font-medium">Back</span>
                    </button>
                    <h1 className={`text-4xl sm:text-5xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Current Subscription
                    </h1>
                    <p className={`text-base sm:text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        View your active plan and subscription details
                    </p>
                </motion.div>

                {planData ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className={`rounded-2xl overflow-hidden shadow-xl ${isDarkMode ? 'bg-gray-800 border-2 border-gray-700' : 'bg-white border-2 border-gray-200'}`}
                    >
                        {/* Plan Header - Green Gradient */}
                        <div className="bg-gradient-to-r from-[#008300] to-green-600 px-6 sm:px-8 py-8 sm:py-10 text-white">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <FaCheckCircle size={28} />
                                        <h2 className="text-3xl sm:text-4xl font-bold">{planData.plan_name}</h2>
                                    </div>
                                    <p className="text-green-100">Your active subscription plan</p>
                                </div>
                            </div>
                        </div>

                        {/* Plan Details Grid */}
                        <div className={`p-6 sm:p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                                {/* Total Credits */}
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className={`rounded-lg p-5 sm:p-6 border-2 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                            <FaCoins size={20} />
                                        </div>
                                        <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Credits</p>
                                    </div>
                                    <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {planData.total_credits.toLocaleString()}
                                    </p>
                                </motion.div>

                                {/* Remaining Credits */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className={`rounded-lg p-5 sm:p-6 border-2 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600'}`}>
                                            <FaCoins size={20} />
                                        </div>
                                        <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remaining Credits</p>
                                    </div>
                                    <p className={`text-3xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                        {planData.remaining_credits.toLocaleString()}
                                    </p>
                                </motion.div>

                                {/* Purchased Date */}
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className={`rounded-lg p-5 sm:p-6 border-2 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                                            <FaCalendarAlt size={20} />
                                        </div>
                                        <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Purchased On</p>
                                    </div>
                                    <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {new Date(planData.purchase_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </p>
                                </motion.div>

                                {/* Expiry Date */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className={`rounded-lg p-5 sm:p-6 border-2 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>
                                            <FaCalendarAlt size={20} />
                                        </div>
                                        <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Expires On</p>
                                    </div>
                                    <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {new Date(planData.expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </p>
                                </motion.div>

                                {/* Billing Cycle */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className={`rounded-lg p-5 sm:p-6 border-2 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-cyan-900/50 text-cyan-400' : 'bg-cyan-100 text-cyan-600'}`}>
                                            <FaRedo size={20} />
                                        </div>
                                        <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Billing Cycle</p>
                                    </div>
                                    <p className={`text-xl font-bold capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {planData.billing_cycle}
                                    </p>
                                </motion.div>

                                {/* Auto Renewal */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className={`rounded-lg p-5 sm:p-6 border-2 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'}`}>
                                            <FaCheckCircle size={20} />
                                        </div>
                                        <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Auto Renewal</p>
                                    </div>
                                    <p className={`text-xl font-bold ${planData.auto_renew ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-red-400' : 'text-red-600')}`}>
                                        {planData.auto_renew ? '✓ Enabled' : '✗ Disabled'}
                                    </p>
                                </motion.div>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className={`px-6 sm:px-8 py-6 border-t ${isDarkMode ? 'bg-gray-700/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleUpgradeClick}
                                className="w-full py-3 sm:py-4 px-6 bg-gradient-to-r from-[#008300] to-green-600 hover:from-[#006400] hover:to-[#008300] text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                            >
                                ⬆️ Upgrade or Change Plan
                            </motion.button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className={`rounded-2xl p-8 sm:p-12 text-center border-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                    >
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <svg 
                                className={`w-12 h-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                                />
                            </svg>
                        </div>
                        <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            No Subscription Yet
                        </h2>
                        <p className={`text-base sm:text-lg mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            You haven't purchased a subscription plan yet. Get started with one of our plans to unlock powerful SEO tools.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleUpgradeClick}
                            className="inline-block px-8 py-4 bg-gradient-to-r from-[#008300] to-green-600 hover:from-[#006400] hover:to-[#008300] text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            📊 View Plans & Subscribe
                        </motion.button>
                    </motion.div>
                )}
            </div>
        </div></>
    )
}
